/**
 * Admin section for "Was ist neu" changelog management.
 *
 * Lets admins list, create, publish/unpublish and delete info entries, and
 * trigger AI-driven generation of new entries from recent git commits.
 *
 * Security: entry title/description are rendered as plain text (JSX
 * interpolation) — never via dangerouslySetInnerHTML or markdown/HTML
 * rendering, since entry content may originate from AI generation.
 */
import { useEffect, useRef, useState } from "preact/hooks";
import { Sparkles, Trash2, Plus } from "lucide-preact";
import { toastError, toastInfo, toastSuccess } from "../../lib/toast";
import { confirmDialog } from "../../lib/confirm";
import {
  ApiError,
  adminListInfoEntries,
  adminCreateInfoEntry,
  adminUpdateInfoEntry,
  adminDeleteInfoEntry,
  adminGenerateInfo,
  adminGetInfoStatus,
  adminSetInfoConfig,
  adminResetInfo,
  listGitRepositories,
  type InfoEntryAdmin,
  type AdminGitRepository,
} from "../../lib/api";
import type { InfoEntryType, InfoGenerationState } from "@juuplan/shared";

// ---------------------------------------------------------------------------
// Shared style constants — mirroring GitAdminSection / AdminDepartments
// ---------------------------------------------------------------------------

const INPUT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] placeholder:text-[color:var(--color-on-surface-variant)]/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";

const SELECT_CLS =
  "w-full rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";

const LABEL_CLS =
  "block text-xs font-medium text-[color:var(--color-on-surface-variant)] mb-1";

const BTN_PRIMARY =
  "rounded bg-[color:var(--color-primary)] px-3 py-2 text-sm font-medium text-[color:var(--color-on-primary)] hover:bg-[color:var(--color-primary-strong)] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";

const BTN_SECONDARY =
  "rounded border border-[color:var(--color-outline-variant)] px-3 py-2 text-sm text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors";

const BTN_DANGER =
  "rounded bg-[color:var(--color-error)] px-2 py-1 text-xs text-[color:var(--color-on-error)] hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)] transition-colors";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const TYPE_LABELS: Record<InfoEntryType, string> = {
  feature: "Neu",
  bugfix: "Behoben",
  improvement: "Verbessert",
  announcement: "Info",
};

// The generation runs server-side fire-and-forget; on the slow prod model a
// full granular run can take several minutes, so the poll window must be long
// (≈5.5 min). If it still exhausts, the run keeps going in the background and
// the list is refreshed with a hint to reload.
const MAX_POLL_ATTEMPTS = 110;
const POLL_INTERVAL_MS = 3000;

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

interface CreateFormProps {
  onCreated: (entry: InfoEntryAdmin) => void;
}

function CreateEntryForm({ onCreated }: CreateFormProps): preact.JSX.Element {
  const [type, setType] = useState<InfoEntryType>("feature");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    setSaving(true);
    try {
      const created = await adminCreateInfoEntry({
        type,
        title: trimmedTitle,
        description: description.trim(),
        is_published: isPublished,
      });
      onCreated(created);
      setTitle("");
      setDescription("");
      setIsPublished(false);
      toastSuccess(`Eintrag „${created.title}" angelegt.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Anlegen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4 space-y-4">
      <h3 class="text-sm font-medium text-[color:var(--color-on-surface)]">
        Neuer Eintrag
      </h3>

      <div>
        <label class={LABEL_CLS} for="wn-type">
          Typ
        </label>
        <select
          id="wn-type"
          value={type}
          onChange={(e) => setType((e.target as HTMLSelectElement).value as InfoEntryType)}
          class={SELECT_CLS}
          aria-label="Typ"
        >
          <option value="feature">Neu</option>
          <option value="bugfix">Behoben</option>
          <option value="improvement">Verbessert</option>
          <option value="announcement">Info</option>
        </select>
      </div>

      <div>
        <label class={LABEL_CLS} for="wn-title">
          Titel
        </label>
        <input
          id="wn-title"
          type="text"
          value={title}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          placeholder="z.B. Neue Kommentarfunktion"
          class={INPUT_CLS}
          aria-label="Titel"
        />
      </div>

      <div>
        <label class={LABEL_CLS} for="wn-description">
          Beschreibung
        </label>
        <textarea
          id="wn-description"
          value={description}
          onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
          placeholder="Kurze Beschreibung…"
          rows={3}
          class={INPUT_CLS}
          aria-label="Beschreibung"
        />
      </div>

      <div class="flex items-center gap-2">
        <input
          id="wn-published"
          type="checkbox"
          checked={isPublished}
          onChange={(e) => setIsPublished((e.target as HTMLInputElement).checked)}
          class="size-4 rounded border-[color:var(--color-outline-variant)] text-[color:var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
        />
        <label for="wn-published" class="text-sm text-[color:var(--color-on-surface)]">
          Veröffentlicht
        </label>
      </div>

      <button
        type="button"
        onClick={() => void handleSubmit()}
        disabled={!title.trim() || saving}
        class={`${BTN_PRIMARY} inline-flex items-center gap-1.5`}
        aria-label="Eintrag hinzufügen"
      >
        <Plus size={14} aria-hidden="true" />
        {saving ? "Erstellt…" : "Eintrag hinzufügen"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entry row
// ---------------------------------------------------------------------------

interface EntryRowProps {
  entry: InfoEntryAdmin;
  onUpdated: (entry: InfoEntryAdmin) => void;
  onDeleted: (id: number) => void;
}

function EntryRow({ entry, onUpdated, onDeleted }: EntryRowProps): preact.JSX.Element {
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toggling, setToggling] = useState(false);

  const handleTogglePublish = async (): Promise<void> => {
    setToggling(true);
    try {
      const updated = await adminUpdateInfoEntry(entry.id, {
        is_published: !entry.is_published,
      });
      onUpdated(updated);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Aktualisieren fehlgeschlagen");
    } finally {
      setToggling(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    setDeleting(true);
    try {
      await adminDeleteInfoEntry(entry.id);
      onDeleted(entry.id);
      toastSuccess(`Eintrag „${entry.title}" gelöscht.`);
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
      setDeleting(false);
      setConfirmDel(false);
    }
  };

  return (
    <li class="border border-[color:var(--color-outline-variant)] rounded-lg bg-[color:var(--color-surface-container-lowest)] p-3 space-y-2">
      <div class="flex flex-wrap items-start gap-x-3 gap-y-1">
        <span class="shrink-0 rounded-full bg-[color:var(--color-surface-container-high)] px-2 py-0.5 text-xs text-[color:var(--color-on-surface-variant)]">
          {TYPE_LABELS[entry.type]}
        </span>

        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-[color:var(--color-on-surface)]">{entry.title}</p>
          {entry.description && (
            <p class="text-xs text-[color:var(--color-on-surface-variant)]">{entry.description}</p>
          )}
        </div>

        <span
          class={`shrink-0 rounded-full px-2 py-0.5 text-xs ${
            entry.is_published
              ? "bg-[color:var(--color-primary-container)] text-[color:var(--color-on-primary-container)]"
              : "bg-[color:var(--color-surface-container-high)] text-[color:var(--color-on-surface-variant)]"
          }`}
        >
          {entry.is_published ? "Veröffentlicht" : "Entwurf"}
        </span>

        <span class="shrink-0 text-xs text-[color:var(--color-on-surface-variant)] opacity-70">
          {entry.source === "ai" ? "KI" : "Manuell"}
        </span>

        <span class="shrink-0 text-xs text-[color:var(--color-on-surface-variant)] opacity-70">
          {new Date(entry.created_at).toLocaleDateString("de-DE")}
        </span>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void handleTogglePublish()}
          disabled={toggling}
          class={`${BTN_SECONDARY} text-xs px-2 py-1`}
          aria-label={
            entry.is_published
              ? `${entry.title} zurückziehen`
              : `${entry.title} veröffentlichen`
          }
        >
          {toggling ? "…" : entry.is_published ? "Zurückziehen" : "Veröffentlichen"}
        </button>

        {confirmDel ? (
          <>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleting}
              class={BTN_DANGER}
              aria-label={`${entry.title} wirklich löschen`}
            >
              {deleting ? "Löscht…" : "Wirklich löschen"}
            </button>
            <button
              type="button"
              onClick={() => setConfirmDel(false)}
              class={`${BTN_SECONDARY} text-xs px-2 py-1`}
              aria-label="Abbrechen"
            >
              Abbrechen
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDel(true)}
            class="rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-error-container)] hover:text-[color:var(--color-on-error-container)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-error)]"
            aria-label={`${entry.title} löschen`}
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Main exported section
// ---------------------------------------------------------------------------

/**
 * "Was ist neu" admin section rendered under /admin/whats-new.
 *
 * Lists info entries (excluding soft-deleted), supports create/publish
 * toggle/delete, and drives AI generation of new entries from git commits
 * with bounded polling of the generation status.
 */
export function WhatsNewAdminSection(): preact.JSX.Element {
  const [entries, setEntries] = useState<InfoEntryAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genStats, setGenStats] = useState<InfoGenerationState["last_run_stats"] | null>(null);

  // Source git repository the generator reads commits from (required).
  const [repos, setRepos] = useState<AdminGitRepository[]>([]);
  const [sourceRepoId, setSourceRepoId] = useState<number | null>(null);
  const [savingRepo, setSavingRepo] = useState(false);

  // Daily auto-generation schedule (Europe/Berlin).
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoHour, setAutoHour] = useState(6);
  const [lastAutoRunAt, setLastAutoRunAt] = useState<string | null>(null);
  const [savingSchedule, setSavingSchedule] = useState(false);

  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    void load();
    void loadConfig();
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  async function loadConfig(): Promise<void> {
    try {
      const [repoList, status] = await Promise.all([
        listGitRepositories(),
        adminGetInfoStatus(),
      ]);
      if (cancelledRef.current) return;
      setRepos(repoList);
      setSourceRepoId(status.git_repository_id);
      setAutoEnabled(status.auto_schedule_enabled);
      setAutoHour(status.auto_schedule_hour);
      setLastAutoRunAt(status.last_auto_run_at);
    } catch {
      // Non-critical — the picker just stays empty and generation is gated.
    }
  }

  async function handleSourceRepoChange(id: number | null): Promise<void> {
    setSavingRepo(true);
    try {
      await adminSetInfoConfig({ git_repository_id: id });
      setSourceRepoId(id);
      toastSuccess("Quell-Repository gespeichert.");
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingRepo(false);
    }
  }

  async function handleScheduleChange(
    next: { enabled?: boolean; hour?: number }
  ): Promise<void> {
    const enabled = next.enabled ?? autoEnabled;
    const hour = next.hour ?? autoHour;
    // Optimistic — reflect the control immediately, roll back on failure.
    const prevEnabled = autoEnabled;
    const prevHour = autoHour;
    setAutoEnabled(enabled);
    setAutoHour(hour);
    setSavingSchedule(true);
    try {
      await adminSetInfoConfig({
        auto_schedule_enabled: enabled,
        auto_schedule_hour: hour,
      });
      toastSuccess(
        enabled
          ? `Automatische Generierung täglich um ${String(hour).padStart(2, "0")}:00 Uhr aktiviert.`
          : "Automatische Generierung deaktiviert."
      );
    } catch (err) {
      setAutoEnabled(prevEnabled);
      setAutoHour(prevHour);
      toastError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSavingSchedule(false);
    }
  }

  async function load(): Promise<void> {
    setLoading(true);
    setLoadError(null);
    try {
      const { entries: list } = await adminListInfoEntries();
      if (!cancelledRef.current) setEntries(list);
    } catch (err) {
      if (!cancelledRef.current) {
        setLoadError(err instanceof Error ? err.message : "Laden fehlgeschlagen");
      }
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }

  const handleCreated = (entry: InfoEntryAdmin): void => {
    setEntries((prev) => [entry, ...prev]);
  };

  const handleUpdated = (updated: InfoEntryAdmin): void => {
    setEntries((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
  };

  const handleDeleted = (id: number): void => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  };

  const pollStatus = async (): Promise<void> => {
    let finished = false;
    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      if (cancelledRef.current) return;
      let status: InfoGenerationState;
      try {
        status = await adminGetInfoStatus();
      } catch {
        // Abbruch der Statusabfrage war stumm — der Nutzer wartete dann
        // unbegrenzt auf ein Ergebnis, das nie kommt.
        toastError("Status der Generierung konnte nicht abgerufen werden.");
        break;
      }
      if (cancelledRef.current) return;
      if (status.last_run_status !== "running") {
        setGenStats(status.last_run_stats);
        if (status.last_run_status === "error") {
          toastError("Generierung fehlgeschlagen — bitte erneut versuchen.");
        }
        await load();
        finished = true;
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
    }
    if (cancelledRef.current) return;
    // Poll window exhausted while still running: the server keeps going in the
    // background — refresh what exists and tell the admin to reload shortly.
    if (!finished) {
      await load();
      toastInfo("Generierung läuft im Hintergrund weiter — bitte in Kürze neu laden.");
    }
    setGenerating(false);
  };

  const handleRebuild = async (): Promise<void> => {
    const ok = await confirmDialog({
      title: "Einträge neu erzeugen",
      message:
        "Alle KI-generierten Einträge werden gelöscht und aus dem gesamten Commit-Verlauf neu erzeugt. Fortfahren?",
      confirmLabel: "Neu erzeugen",
      danger: true,
    });
    if (!ok) return;
    setGenerating(true);
    setGenStats(null);
    try {
      await adminResetInfo();
    } catch (err) {
      toastError(err instanceof Error ? err.message : "Zurücksetzen fehlgeschlagen");
      setGenerating(false);
      return;
    }
    await handleGenerateInner();
  };

  const handleGenerate = async (): Promise<void> => {
    setGenerating(true);
    setGenStats(null);
    await handleGenerateInner();
  };

  const handleGenerateInner = async (): Promise<void> => {
    try {
      await adminGenerateInfo();
    } catch (err) {
      if (err instanceof ApiError && err.status === 503) {
        toastError("KI ist deaktiviert.");
      } else if (err instanceof ApiError && err.status === 409) {
        toastError("Generierung läuft bereits.");
      } else {
        toastError(err instanceof Error ? err.message : "Generierung fehlgeschlagen");
      }
      setGenerating(false);
      return;
    }
    await pollStatus();
  };

  return (
    <section class="space-y-8 max-w-3xl">
      <div>
        <h2 class="text-base font-semibold text-[color:var(--color-on-surface)] mb-3">
          Aus Commits generieren
        </h2>

        {/* Source repository — REQUIRED before generation can run. */}
        <div class="mb-3 max-w-md">
          <label class={LABEL_CLS} for="wn-source-repo">
            Quell-Repository
          </label>
          <select
            id="wn-source-repo"
            value={sourceRepoId ?? ""}
            disabled={savingRepo}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value;
              void handleSourceRepoChange(v === "" ? null : parseInt(v, 10));
            }}
            class={SELECT_CLS}
            aria-label="Quell-Repository für die Generierung"
          >
            <option value="">Kein Repository gewählt</option>
            {repos.map((r) => (
              <option key={r.id} value={r.id}>
                {r.full_name}
              </option>
            ))}
          </select>
          <p class="text-label text-[color:var(--color-on-surface-variant)] mt-1">
            Die „Was ist neu"-Einträge werden aus den Commits dieses Repositories erzeugt und
            automatisch veröffentlicht.
          </p>
        </div>

        {/* Daily auto-generation schedule (Europe/Berlin). */}
        <div class="mb-4 max-w-md rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-3 space-y-2">
          <div class="flex items-center gap-2">
            <input
              id="wn-auto-enabled"
              type="checkbox"
              checked={autoEnabled}
              disabled={savingSchedule || sourceRepoId === null}
              onChange={(e) =>
                void handleScheduleChange({
                  enabled: (e.target as HTMLInputElement).checked,
                })
              }
              class="size-4 rounded border-[color:var(--color-outline-variant)] text-[color:var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:opacity-50"
            />
            <label
              for="wn-auto-enabled"
              class="text-sm text-[color:var(--color-on-surface)]"
            >
              Täglich automatisch generieren
            </label>
          </div>

          <div class="flex items-center gap-2 pl-6">
            <label class="text-xs text-[color:var(--color-on-surface-variant)]" for="wn-auto-hour">
              um
            </label>
            <select
              id="wn-auto-hour"
              value={autoHour}
              disabled={savingSchedule || !autoEnabled || sourceRepoId === null}
              onChange={(e) =>
                void handleScheduleChange({
                  hour: parseInt((e.target as HTMLSelectElement).value, 10),
                })
              }
              class={`${SELECT_CLS} w-auto`}
              aria-label="Uhrzeit der automatischen Generierung"
            >
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>
                  {String(h).padStart(2, "0")}:00
                </option>
              ))}
            </select>
            <span class="text-xs text-[color:var(--color-on-surface-variant)]">
              Uhr (Europe/Berlin)
            </span>
          </div>

          {sourceRepoId === null && (
            <p class="text-label text-[color:var(--color-on-surface-variant)] pl-6">
              Zuerst ein Quell-Repository wählen.
            </p>
          )}
          {autoEnabled && lastAutoRunAt && (
            <p class="text-label text-[color:var(--color-on-surface-variant)] pl-6">
              Zuletzt automatisch:{" "}
              {new Date(lastAutoRunAt).toLocaleString("de-DE")}
            </p>
          )}
        </div>

        <div class="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => void handleGenerate()}
            disabled={generating || sourceRepoId === null}
            title={sourceRepoId === null ? "Zuerst ein Quell-Repository wählen" : undefined}
            class={`${BTN_PRIMARY} inline-flex items-center gap-1.5`}
            aria-label="Aus Commits generieren"
          >
            <Sparkles size={14} aria-hidden="true" />
            {generating ? "Generiere…" : "Aus Commits generieren"}
          </button>
          <button
            type="button"
            onClick={() => void handleRebuild()}
            disabled={generating || sourceRepoId === null}
            title={
              sourceRepoId === null
                ? "Zuerst ein Quell-Repository wählen"
                : "Alle KI-Einträge löschen und aus dem gesamten Verlauf neu erzeugen"
            }
            class={`${BTN_SECONDARY} inline-flex items-center gap-1.5`}
            aria-label="KI-Einträge neu aufbauen"
          >
            {generating ? "Arbeitet…" : "Neu aufbauen"}
          </button>
          {genStats && (
            <span class="text-xs text-[color:var(--color-on-surface-variant)]">
              {genStats.commits_scanned} Commits, {genStats.entries_created} Einträge erstellt
            </span>
          )}
        </div>
      </div>

      <div>
        <h2 class="text-base font-semibold text-[color:var(--color-on-surface)] mb-3">
          Einträge
        </h2>

        {loading ? (
          <p class="text-sm text-[color:var(--color-on-surface-variant)]">Lädt…</p>
        ) : loadError ? (
          <p class="text-sm text-[color:var(--color-error)]">Fehler: {loadError}</p>
        ) : entries.length === 0 ? (
          <p class="text-sm text-[color:var(--color-on-surface-variant)] italic mb-4">
            Noch keine Einträge.
          </p>
        ) : (
          <ul class="space-y-3 mb-4">
            {entries.map((entry) => (
              <EntryRow
                key={entry.id}
                entry={entry}
                onUpdated={handleUpdated}
                onDeleted={handleDeleted}
              />
            ))}
          </ul>
        )}

        {!loading && !loadError && <CreateEntryForm onCreated={handleCreated} />}
      </div>
    </section>
  );
}
